// Story 1.27 — TanStack Query mutation for POST /api/v1/admin/creator-applications/:id/approve.
//
// Generates a fresh Idempotency-Key per click (UUIDs via crypto.randomUUID
// — replays of the SAME click are safe, retries reuse the key
// implicitly through the cached response).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type ApproveResponse = {
  data: {
    application_id: string;
    user_id: string;
    status: "approved";
    assigned_handle: string;
    public_profile_url: string;
    approved_at: string;
  };
};

export type ApproveError = {
  code: string;
  message: string;
  current_status?: string;
  status: number;
};

async function approveApplication(
  applicationId: string
): Promise<ApproveResponse> {
  const response = await fetch(
    `/api/v1/admin/creator-applications/${applicationId}/approve`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify({}),
    }
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: ApproveError = {
      code: "ADMIN_APPROVE_FAILED",
      message: `Approve failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          current_status: raw.error.current_status,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as ApproveResponse;
}

export function useApproveCreatorApplication(applicationId: string) {
  const queryClient = useQueryClient();
  return useMutation<ApproveResponse, ApproveError>({
    mutationFn: () => approveApplication(applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-creator-applications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-creator-application-detail", applicationId],
      });
    },
  });
}
